import type { Data } from '../types';

type RoundStatsPanelProps = {
  data: Data | null;
};

type TeamTone = 'ct' | 't';
type TooltipAlign = 'left' | 'center' | 'right';

const normalizeSide = (value: string | undefined): TeamTone | null => {
  if (!value) return null;
  const normalized = value.trim().toLowerCase();
  if (normalized === 'ct' || normalized.includes('counter')) return 'ct';
  if (normalized === 't' || normalized.includes('terror')) return 't';
  return null;
};

const buildTeamSideByName = (data: Data | null) => {
  const tones = new Map<string, TeamTone>();
  if (!data) return tones;
  for (const [name, team] of Object.entries(data.teams ?? {})) {
    const tone = normalizeSide(team.startedOn);
    if (tone) tones.set(name, tone);
  }
  return tones;
};

const formatDuration = (seconds: number) => {
  const safe = Math.max(0, Math.round(seconds));
  const minutes = Math.floor(safe / 60);
  const remainder = safe % 60;
  if (minutes === 0) return `${remainder}s`;
  return `${minutes}:${remainder.toString().padStart(2, '0')}`;
};

const clampPercent = (value: number, min = 8, max = 100) =>
  Math.min(max, Math.max(min, value));

const getTooltipAlign = (position: number): TooltipAlign => {
  if (position <= 14) return 'left';
  if (position >= 86) return 'right';
  return 'center';
};

const getTooltipAlignClass = (align: TooltipAlign) => {
  if (align === 'left') return 'left-0';
  if (align === 'right') return 'right-0';
  return 'left-1/2 -translate-x-1/2';
};

const formatWinReason = (reason: string | undefined) => {
  switch (reason) {
    case 'kills':
      return 'Kills';
    case 'detonation':
      return 'Detonation';
    case 'defused':
      return 'Defused';
    case 'time':
      return 'Time';
    default:
      return 'Unknown';
  }
};

export default function RoundStatsPanel({ data }: RoundStatsPanelProps) {
  const rounds = data?.rounds ?? [];
  const hasData = rounds.length > 0;
  const teamSideByName = buildTeamSideByName(data);
  const ctTeamName =
    Array.from(teamSideByName.entries()).find(([, tone]) => tone === 'ct')?.[0] ??
    null;
  const tTeamName =
    Array.from(teamSideByName.entries()).find(([, tone]) => tone === 't')?.[0] ?? null;
  const maxLength = Math.max(1, ...rounds.map((round) => round.length));
  const totalLength = rounds.reduce((sum, round) => sum + round.length, 0);
  const avgLength = rounds.length ? totalLength / rounds.length : 0;
  const plantRounds = rounds.filter((round) => round.plantTime !== undefined).length;
  const defuseRounds = rounds.filter((round) => round.defuseTime !== undefined).length;
  const plantRate = rounds.length ? Math.round((plantRounds / rounds.length) * 100) : 0;
  const defuseRate = rounds.length ? Math.round((defuseRounds / rounds.length) * 100) : 0;

  return (
    <div
      className={`relative col-span-6 overflow-visible rounded-[20px] border border-stroke bg-card p-5 shadow-[0_18px_32px_rgba(0,0,0,0.45)] animate-rise ${
        !hasData ? 'opacity-70' : ''
      } max-[960px]:col-span-1`}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="text-[18px] font-semibold">Round stats</h3>
          <p className="mt-1 text-[13px] text-muted">
            Round length with plant and defuse timing markers
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-4">
        <div className="grid gap-3 min-[720px]:grid-cols-3">
          <div className="rounded-2xl border border-stroke bg-[rgba(53,25,44,0.85)] p-3.5">
            <div className="text-[18px] font-bold">{formatDuration(avgLength)}</div>
            <div className="mt-1 text-[11px] uppercase tracking-[1px] text-muted">
              Avg round length
            </div>
          </div>
          <div className="rounded-2xl border border-stroke bg-[rgba(53,25,44,0.85)] p-3.5">
            <div className="text-[18px] font-bold">
              {plantRounds}
              <span className="ml-1 text-[12px] text-muted">({plantRate}%)</span>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[1px] text-muted">
              Bomb plants
            </div>
          </div>
          <div className="rounded-2xl border border-stroke bg-[rgba(53,25,44,0.85)] p-3.5">
            <div className="text-[18px] font-bold">
              {defuseRounds}
              <span className="ml-1 text-[12px] text-muted">({defuseRate}%)</span>
            </div>
            <div className="mt-1 text-[11px] uppercase tracking-[1px] text-muted">
              Bomb defuses
            </div>
          </div>
        </div>

        <div className="flex flex-wrap gap-3 text-[11px] text-muted">
          <span className="inline-flex items-center gap-1.5 before:h-2 before:w-2 before:rounded-full before:bg-ct before:content-['']">
            {(ctTeamName ?? 'CT') + ' win'}
          </span>
          <span className="inline-flex items-center gap-1.5 before:h-2 before:w-2 before:rounded-full before:bg-t before:content-['']">
            {(tTeamName ?? 'T') + ' win'}
          </span>
          <span className="inline-flex items-center gap-1.5">
            <img
              src="/C4_ticking_source.webp"
              alt="Plant"
              className="h-3 w-3"
            />
            Plant
          </span>
          <span className="inline-flex items-center gap-1.5">
            <img
              src="/Csgo_DefusekitScoreboardicon.webp"
              alt="Defuse"
              className="h-3 w-3"
            />
            Defuse
          </span>
          <span className="inline-flex items-center gap-1.5">
            <img
              src="/C4_detonated_scoreboard.webp"
              alt="Defuse"
              className="h-3 w-3"
            />
            Detonation
          </span>
        </div>

        <div className="scrollbar-panel grid max-h-[450px] gap-3 overflow-y-auto pb-12 pr-2">
          {rounds.map((round) => {
            const width = clampPercent((round.length / maxLength) * 100);
            const plantPosition =
              round.plantTime !== undefined && maxLength > 0
                ? clampPercent((round.plantTime / maxLength) * 100, 0, 100)
                : null;
            const defusePosition =
              round.defuseTime !== undefined && maxLength > 0
                ? clampPercent((round.defuseTime / maxLength) * 100, 0, 100)
                : null;
            const detonationPosition =
              round.detonationTime !== undefined && maxLength > 0
                ? clampPercent((round.detonationTime / maxLength) * 100, 0, 100)
                : null;
            const plantAlign =
              plantPosition !== null ? getTooltipAlign(plantPosition) : 'center';
            const defuseAlign =
              defusePosition !== null ? getTooltipAlign(defusePosition) : 'center';
            const detonationAlign =
              detonationPosition !== null ? getTooltipAlign(detonationPosition) : 'center';
            const winnerTone =
              teamSideByName.get(round.winner) ?? normalizeSide(round.winner);
            const barClass =
              winnerTone === 'ct'
                ? 'bg-[linear-gradient(90deg,rgba(255,254,62,0.8),rgba(255,254,62,0.35))]'
                : winnerTone === 't'
                  ? 'bg-[linear-gradient(90deg,rgba(168,85,247,0.9),rgba(168,85,247,0.35))]'
                  : 'bg-[rgba(255,255,255,0.15)]';

            return (
              <div
                className="grid grid-cols-[46px_minmax(0,1fr)_58px] items-center text-[12px]"
                key={round.id}
              >
                <div className="font-semibold text-muted">R{round.id}</div>
                <div className="grid gap-1">
                  <div className="relative h-2.5 rounded-full bg-[rgba(60,27,46,0.7)]">
                    <div className={`h-full rounded-full ${barClass}`} style={{ width: `${width}%` }} />
                    {plantPosition !== null && (
                      <span
                        className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 hover:z-30"
                        style={{ left: `${plantPosition}%` }}
                      >
                        <img
                          src="/C4_ticking_source.webp"
                          alt="Plant"
                          className="block h-5 w-5"
                        />
                        <span
                          className={`pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-lg border border-stroke bg-[rgba(23,10,19,0.9)] px-2 py-1 text-[11px] font-semibold text-ink opacity-0 transition group-hover:opacity-100 z-30 ${getTooltipAlignClass(
                            plantAlign,
                          )}`}
                        >
                          <span className="block text-ink">Plant</span>
                          <span className="mt-0.5 block text-[10px] text-muted">
                            Time: {formatDuration(round.plantTime ?? 0)}
                          </span>
                          {round.planter && (
                            <span className="mt-0.5 block text-[10px] text-muted">
                              Player: {round.planter}
                            </span>
                          )}
                          {round.plantSite && (
                            <span className="mt-0.5 block text-[10px] text-muted">
                              Site {round.plantSite}
                            </span>
                          )}
                        </span>
                      </span>
                    )}
                    {defusePosition !== null && (
                      <span
                        className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 hover:z-30"
                        style={{ left: `${defusePosition}%` }}
                      >
                        <img
                          src="/Csgo_DefusekitScoreboardicon.webp"
                          alt="Defuse"
                          className="block h-5 w-5"
                        />
                        <span
                          className={`pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-lg border border-stroke bg-[rgba(23,10,19,0.9)] px-2 py-1 text-[11px] font-semibold text-ink opacity-0 transition group-hover:opacity-100 z-30 ${getTooltipAlignClass(
                            defuseAlign,
                          )}`}
                        >
                          <span className="block text-ink">Defuse</span>
                          <span className="mt-0.5 block text-[10px] text-muted">
                            Time: {formatDuration(round.defuseTime ?? 0)}
                          </span>
                          {round.defuse && (
                            <span className="mt-0.5 block text-[10px] text-muted">
                              Player: {round.defuse}
                            </span>
                          )}
                        </span>
                      </span>
                    )}
                    {detonationPosition !== null && (
                      <span
                        className="group absolute top-1/2 -translate-x-1/2 -translate-y-1/2 hover:z-30"
                        style={{ left: `${detonationPosition}%` }}
                      >
                        <img
                          src="/C4_detonated_scoreboard.webp"
                          alt="Detonation"
                          className="block h-5 w-5"
                        />
                        <span
                          className={`pointer-events-none absolute top-full mt-2 whitespace-nowrap rounded-lg border border-stroke bg-[rgba(23,10,19,0.9)] px-2 py-1 text-[11px] font-semibold text-ink opacity-0 transition group-hover:opacity-100 z-30 ${getTooltipAlignClass(
                            detonationAlign,
                          )}`}
                        >
                          <span className="block text-ink">Detonation</span>
                          <span className="mt-0.5 block text-[10px] text-muted">
                            Time: {formatDuration(round.detonationTime ?? 0)}
                          </span>
                        </span>
                      </span>
                    )}
                  </div>
                  <div className="flex flex-wrap items-center gap-2 text-[11px] text-muted">
                    <span className="font-semibold text-ink">{round.winner}</span>
                    <span className="rounded-full border border-stroke bg-[rgba(31,13,26,0.7)] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-[1px] text-muted">
                      Win: {formatWinReason(round.winReason)}
                    </span>
                    {round.planter && <span>Plant: {round.planter}</span>}
                    {round.defuse && <span>Defuse: {round.defuse}</span>}
                    {round.plantSite && <span>Site {round.plantSite}</span>}
                  </div>
                </div>
                <div className="text-right text-muted">{formatDuration(round.length)}</div>
              </div>
            );
          })}
        </div>
      </div>

      {!hasData && (
        <div className="absolute inset-4 grid place-items-center rounded-2xl border border-dashed border-accent/35 bg-[rgba(31,13,26,0.92)] text-center text-[14px] font-semibold text-muted">
          Upload a log file to view round stats.
        </div>
      )}
    </div>
  );
}
